/**
 * ProductCaptureView — Camera view for product-based (AI-research) classification.
 * Captures multiple photos, shows a progress screen while the API runs,
 * then navigates to the results page.
 */

import React, { useState, useCallback, useEffect, useRef } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Badge, Button, Icon, Thumbnail } from "@amzn/meridian";
import cameraTokens from "@amzn/meridian-tokens/base/icon/camera";
import FlashlightButton from "src/components/pages/inspection/components/FlashlightButton";
import ZoomControl from "src/components/pages/inspection/components/ZoomControl";
import { captureVideoFrame } from "src/components/pages/inspection/utils/videoUtils";
import { useCamera } from "src/hooks/useCamera";
import PillButton from "src/components/UI/PillButton";
import { ScanModeToggle, ScanMode, ScannerTopOverlay, AI_RESEARCH_INSIGHT_MESSAGE } from "./ScannerSharedComponents";
import "src/components/pages/configurationDrivenInspections/steps/SimpleCamera.scss";
import ImagePreviewOverlay from "./ImagePreviewOverlay";
import ClassificationProgressScreen, { AI_RESEARCH_TOTAL_DURATION_MS } from "./ClassificationProgressScreen";
import { ClassificationType, CapturedImage, AIResearchResponse } from "src/types/classificationTypes";
import { useNavigate } from "react-router-dom";
import "src/components/UI/cameraOverlay.scss";
import "./classification.scss";
import { classifyProductFromImages } from "src/services/itemClassificationService";
import { useAppSelector } from "src/store/store";
import { extractSiteCode } from "src/constants/constants";
interface ProductCaptureViewProps {
  activeMode: ScanMode;
  onModeChange: (mode: ScanMode) => void;
}


const ProductCaptureView: React.FC<ProductCaptureViewProps> = ({
  activeMode, onModeChange,
}) => {
  // Camera stream — shared hook handles getUserMedia, cleanup, and error states
  const { videoRef, isReady, error, setError } = useCamera();

  // Captured product images — each entry holds the File (for API) and a blob URL (for display)
  const [capturedImages, setCapturedImages] = useState<CapturedImage[]>([]);

  const [showPreview, setShowPreview] = useState(false);

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [apiData, setApiData] = useState<AIResearchResponse | null>(null);
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);

  const navigate = useNavigate();
  const { siteLocation } = useAppSelector((state) => state.user);
  const siteCode = extractSiteCode(siteLocation);

  // Timer ref for animation countdown — cleared on unmount
  const timeoutRef = useRef<number | undefined>(undefined);

  // Keep a ref to the latest capturedImages so the cleanup closure always has the current list
  const capturedImagesRef = useRef<CapturedImage[]>([]);
  capturedImagesRef.current = capturedImages;

  // Cleanup: cancel the progress timer and revoke blob URLs on unmount
  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
      capturedImagesRef.current.forEach((img) => URL.revokeObjectURL(img.displayUrl));
    };
  }, []);

  // Capture a single frame from the video feed and store as File + display URL
  const handleCapture = useCallback(async () => {
    if (!videoRef.current || !isReady) return;
    try {
      const file = await captureVideoFrame(videoRef.current);
      // Create a lightweight blob URL pointing to the File in memory — used for <img> display
      // without the overhead of base64 encoding. Must be revoked when no longer needed.
      const displayUrl = URL.createObjectURL(file);
      setCapturedImages((prev) => [...prev, { file, displayUrl }]);
    } catch {
      console.error("[ProductCapture] Failed to capture frame");
      setError("Failed to capture frame. Please try again.");
    }
  }, [isReady, capturedImages.length]);
  const handleClassify = async () => {
    setIsAnalyzing(true);
    setApiData(null);
    setIsAnimationComplete(false);

    // Start animation timer — marks animation done after all steps play
    timeoutRef.current = window.setTimeout(() => setIsAnimationComplete(true), AI_RESEARCH_TOTAL_DURATION_MS + 300);

    const imageFiles = capturedImages.map((e) => e.file);

    try {
      const data = await classifyProductFromImages(imageFiles, siteCode);
      setApiData(data);
    } catch (err) {
      console.error("[ProductCapture] AI-research classification failed:", err);
      clearTimeout(timeoutRef.current);
      setIsAnalyzing(false);
    }
  };

  // Navigate only when both animation is done AND API has responded
  useEffect(() => {
    if (isAnimationComplete && apiData !== null) {
      navigate("/classification/aiResults", { state: { data: apiData } });
    }
  }, [isAnimationComplete, apiData]);

  if (isAnalyzing) {
    return (
      <ClassificationProgressScreen
        classificationConfig={{
          type: ClassificationType.AI_RESEARCH,
          capturedImages: capturedImages.map((img) => img.displayUrl),
          aiInsightMessage: AI_RESEARCH_INSIGHT_MESSAGE,
        }}
        isReadyToNavigate={apiData!== null && isAnimationComplete}
      />
    );
  }

  return (

    <Column className="simple-camera-viewport pwa-fullscreen-viewport" height="100vh" spacing="none">

      {/* Live camera feed */}
      <video ref={videoRef} className="simple-camera-video" autoPlay muted playsInline />

      {/* Camera error message */}
      {error && (
        <div className="simple-camera-error">
          <Text color="error">{error}</Text>
        </div>
      )}

      {!isReady && !error && (
        <div className="simple-camera-loading">
          <Text color="inverted">Initializing camera...</Text>
        </div>
      )}

      {/* Top overlay: back button + title + guidance text */}
      <ScannerTopOverlay title="AI Product Classification">
        <Row className="camera-pill-bg" alignmentHorizontal="center" spacingInset="300">
          <Text color="inverted" alignment="center">
            Capture multiple photos covering all sides of the product for better accuracy.
          </Text>
        </Row>
      </ScannerTopOverlay>

      {/* Bottom overlay: camera controls + mode toggle / classify button */}
      <Column className="camera-overlay camera-overlay--bottom" spacingInset="300 400" alignmentHorizontal="center" spacing="400">

        {/* Camera controls row: last-captured thumbnail | capture button | flashlight */}
        <Row widths={["fill", "fill", "fill"]} alignmentVertical="center" width="100%">
          <Row alignmentHorizontal="start">
            {capturedImages.length > 0 ? (
              <div className="capture-thumbnail__wrapper" onClick={() => setShowPreview(true)}>
                <Thumbnail source={capturedImages[capturedImages.length - 1].displayUrl} size="medium" type="square" />
                <Row className="capture-thumbnail__badge">
                  <Badge value={capturedImages.length} />
                </Row>
              </div>
            ) : null}
          </Row>

          <Row alignmentHorizontal="center">
            <div style={{ backgroundColor: "white", borderRadius: "50%" }}>
              <Button type="icon" size="xlarge" circular={true} onClick={handleCapture} disabled={!isReady}>
                <Icon tokens={cameraTokens} />
              </Button>
            </div>
          </Row>

          <Row alignmentHorizontal="end">
            <FlashlightButton videoRef={videoRef} />
          </Row>
        </Row>

        <ZoomControl videoRef={videoRef} />

        {/* Show CLASSIFY button once images are captured, otherwise show Barcode/Product toggle */}
        <Row spacingInset="400">
          {capturedImages.length > 0 ? (
            <Row spacingInset="300">
              <PillButton text="CLASSIFY" color="success" onClick={() => handleClassify()} />
            </Row>
          ) : (
            <ScanModeToggle activeMode={activeMode} onModeChange={onModeChange} />
          )}
        </Row>
      </Column>


      {showPreview && (
        <ImagePreviewOverlay
          images={capturedImages.map((img) => img.displayUrl)}
          onClose={() => setShowPreview(false)}
          onDelete={(index) => {
            // Revoke the blob URL of the deleted image to free memory
            URL.revokeObjectURL(capturedImages[index].displayUrl);
            const updated = capturedImages.filter((_, i) => i !== index);
            setCapturedImages(updated);
            if (updated.length === 0) setShowPreview(false);
          }}
          onConfirm={() => { setShowPreview(false); handleClassify(); }}
        />
      )}
    </Column>
  );
};

export default ProductCaptureView;
